/**
 * @packageDocumentation
 * Provides utilities for scaling PixiJS applications to fit various devices and orientations while maintaining aspect ratios.
 */
import { Application } from "pixi.js";
import { BottomBar } from "./Game/bottomBar";
import { Assets } from 'pixi.js'; 
import { BottomcanvasProperties } from "./config";
import { PortraitAdjustFun,landScapeAdjustFun,PortraitAdjust_preloader,
  landscapeAdjust_preloader } from "./app";
/**
 * Interface defining the properties required for app resizing
 * @interface
 */
interface appDataType {
    /** The PIXI Application instance */
    app:Application,
    /** Width of the application */
    app_width:number,
    /** Height of the application */
    app_height:number,
    /** Flag to enable/disable device-specific resizing */
    resize_to_device:number,
    /** Y-axis transform origin percentage */
    transformOrigin_Y:number,
    /** Y-axis translation percentage */
    translate_Y:number,
    /** Top position of the app as percentage */
    app_top:number,
    /** Name identifier for the app */
    app_name:string
}

/**
 * Handles the resizing and positioning of PIXI applications for different screen orientations and device sizes
 * @param appData - Configuration object containing app properties
 * @returns Cleanup function to remove event listeners when component unmounts
 */
export function StartAppResize(appData: appDataType) {
 
  document.body.style.backgroundImage =  "url('./assets/Images/bg_preloader_wide.jpg')";
  document.body.style.backgroundPosition = "center bottom"
 
  
  const {
    app,
    app_width,
    app_height,
    resize_to_device,
    transformOrigin_Y,
    translate_Y,
    app_top,
    app_name
  } = appData;

  /**
   * Handles the resizing logic based on screen orientation and dimensions
   * Adjusts canvas properties and app stage for optimal display
   */
  function resizer(){
    const newWidth = window.visualViewport
    ? window.visualViewport.width / (window.visualViewport.scale)
    : window.innerWidth;
  
  const newHeight = window.visualViewport
    ? window.visualViewport.height / (window.visualViewport.scale)
    : window.innerHeight;
  const ratio = newWidth/ newHeight;

    const scaleX = newWidth / app_width;
    const scaleY = newHeight / app_height;
    const scale = Math.min(scaleX, scaleY);

    const isLandscape = window.matchMedia("(orientation:landscape)").matches;
    const isPortrait = window.matchMedia("(orientation:portrait)").matches;

    const canvas = app.canvas;

      
    canvas.style.width = app_width + "px";
    canvas.style.height = app_height + "px";

    canvas.style.position = "absolute";
    canvas.style.top = app_top + "%";
    canvas.style.left = "50%";
    canvas.style.transformOrigin = `50% ${transformOrigin_Y}%`;
    canvas.style.transform = `translate(-50%, -${translate_Y}%) scale(${scale})`;

    if (isLandscape) {

      if (app_name === "bottom_app") { 
        BottomBar.AlignLandscape();
      }else{
        if(landscapeAdjust_preloader){
          landscapeAdjust_preloader() 
        } 
        if(landScapeAdjustFun){
          landScapeAdjustFun() 
        }
        app.stage.scale.set(1) 
        app.stage.x = 0
        app.stage.y = 0 
      }
      app.renderer.resize(app_width, app_height); 

      if (app_name === "app_bg") {
        const canvas = app.canvas;
      
        // Always fill the screen with no scaling artifacts
        const screenWidth = window.visualViewport ? window.visualViewport.width : window.innerWidth;
        const screenHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      
        const designWidth = 1776;
        const designHeight = 768;
      
        const scaleX = screenWidth / designWidth;
        const scaleY = screenHeight / designHeight;
        const scale = Math.max(scaleX, scaleY); // Use max for full bleed (like background-size: cover)
      
        const offsetX = (screenWidth - designWidth * scale) / 2;
        const offsetY = (screenHeight - designHeight * scale) / 2;
      
        // Position and scale to behave like a full background image
        canvas.style.position = 'absolute';
        canvas.style.top = `${offsetY}px`;
        canvas.style.left = `${offsetX}px`;
        canvas.style.width = `${designWidth}px`;
        canvas.style.height = `${designHeight}px`;
        canvas.style.transformOrigin = 'top left';
        canvas.style.transform = `scale(${scale})`;
        canvas.style.zIndex = '0'; // behind all
      
        // Resize renderer to original design resolution (no scale)
        app.renderer.resize(designWidth, designHeight);
      } 
    }

    if (isPortrait) {
      if (app_name === "bottom_app") { 
        const bottomAppHeight = 281; // Or make it dynamic if needed
        canvas.style.height = bottomAppHeight + "px";
        BottomBar.AlignPortrait();
        app.renderer.resize(app_width, bottomAppHeight);
      } else if (app_name === "app") { 
        if(PortraitAdjust_preloader){
          PortraitAdjust_preloader() 
        };
        canvas.style.top = app_top-5 + "%"; 
        if(PortraitAdjustFun){
          PortraitAdjustFun() 
        };
        canvas.style.transform = `translate(-50%, -${translate_Y}%) scale(${scale*3})`;
        app.stage.scale.set(0.9) 
        app.stage.x = 70
        app.stage.y = -25+70 

        if(ratio>.525&&ratio<0.55){
          app.stage.scale.set(0.95)
          app.stage.x = 35 
          app.stage.y = -25+70 

        }
        if(ratio>.54&&ratio<0.58){
          app.stage.scale.set(0.9)
          app.stage.x = 70 
          app.stage.y = -25+70  
        }
        if(ratio>.57&&ratio<0.61){
          app.stage.scale.set(0.86)
          app.stage.x = 96 
          app.stage.y = -25+80  
        }
        if(ratio>.6&&ratio<=.635){
          app.stage.scale.set(0.82)
          app.stage.x = 120 
          app.stage.y = -25+90  
        }
        if(ratio>.635&&ratio<=.65){
          app.stage.scale.set(0.78)
          app.stage.x = 150 
          app.stage.y = -25+110  
        }
        if(ratio>.65&&ratio<=.69){
          app.stage.scale.set(0.73)
          app.stage.x = 185 
          app.stage.y = -25+120  
        }
        if(ratio>.69&&ratio<=.73){
          app.stage.scale.set(0.7)
          app.stage.x = 205
          app.stage.y = -25+130  
        }
        if(ratio>.73&&ratio<=.77){
          app.stage.scale.set(0.65)
          app.stage.x = 240
          app.stage.y = -25+145 
        }

        if(ratio>.77&&ratio<=.83){
          app.stage.scale.set(0.6)
          app.stage.x = 273
          app.stage.y = -25+180  
        }

        if(ratio>.83&&ratio<=.875){
          app.stage.scale.set(0.55)
          app.stage.x = 310
          app.stage.y = -25+180  
        }
        if(ratio>.875&&ratio<=.93){
          app.stage.scale.set(0.52)
          app.stage.x = 330
          app.stage.y = -25+195  
        }
        if(ratio>.93&&ratio<=.97){
          app.stage.scale.set(0.5)
          app.stage.x = 340
          app.stage.y = -25+205 
        }
        if(ratio>.97&&ratio<=1){
          app.stage.scale.set(0.47)
          app.stage.x = 360
          app.stage.y = -25+225 
        }

      } else if (app_name === "app_bg") {
        const canvas = app.canvas; 
        // Always fill the screen with no scaling artifacts
        const screenWidth = window.visualViewport ? window.visualViewport.width : window.innerWidth;
        const screenHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      
        const designWidth = 1776;
        const designHeight = 768;
      
        const scaleX = screenWidth / designWidth;
        const scaleY = screenHeight / designHeight;
        const scale = Math.max(scaleX, scaleY); // Use max for full bleed (like background-size: cover)
      
        const offsetX = (screenWidth - designWidth * scale) / 2;
        const offsetY = (screenHeight - designHeight * scale) / 2;
      
        // Position and scale to behave like a full background image
        canvas.style.position = 'absolute';
        canvas.style.top = `${offsetY}px`;
        canvas.style.left = `${offsetX}px`;
        canvas.style.width = `${designWidth}px`;
        canvas.style.height = `${designHeight}px`;
        canvas.style.transformOrigin = 'top left';
        canvas.style.transform = `scale(${scale})`;
        canvas.style.zIndex = '0'; // behind all
      
        // Resize renderer to original design resolution (no scale)
        app.renderer.resize(designWidth, designHeight);
      }
    }
  };
  
  if (resize_to_device) {
    let timeOut:any; 
    window.addEventListener("resize", resizer);
    resizer();
    // Return a cleanup function (optional for single-page app, essential for SPA frameworks)
    return () => {
      window.removeEventListener("resize", resizer);
    };
  } else {
    const canvas = app.canvas;
    canvas.style.transform = `translate(-50%, -${translate_Y}%) scale(1)`;
    canvas.style.position = "absolute";
    canvas.style.top = app_top + "%";
    canvas.style.left = "50%";
    canvas.style.transformOrigin = `50% ${transformOrigin_Y}%`;
  }
}




